import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { Badge, Breadcrumbs } from '@unified-checkout/design-system';

import type { IBadge } from 'libs/design-system/src/types/Badge/badge';
import type { ISingleHeaderProps } from '@app/dashboard/types/innerLayout/chargebacks';
import { useLangStore } from '@app/dashboard/store/lang';
import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';

import styles from './header.module.scss';

const Header = ({
  status,
  amount,
  created_at,
  currencyFormatter,
}: ISingleHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { lang } = useLangStore();

  const getBadgeProps = (status: ISingleHeaderProps['status']): IBadge => {
    const STATUS_COLOR_MAP = {
      Open: {
        text: t('innerLayout.chargeBacks.status.open'),
        color: 'yellow',
      },
      'Merchant Contested': {
        text: t('innerLayout.chargeBacks.status.merchantContested'),
        color: 'gray',
      },
      'Merchant Accepted': {
        text: t('innerLayout.chargeBacks.status.merchantAccepted'),
        color: 'primary',
      },
      'Merchant Lost': {
        text: t('innerLayout.chargeBacks.status.merchantLost'),
        color: 'red',
      },
      'Merchant Won': {
        text: t('innerLayout.chargeBacks.status.merchantWon'),
        color: 'green',
      },
    };

    return {
      text: STATUS_COLOR_MAP[status].text,
      color: STATUS_COLOR_MAP[status].color as IBadge['color'],
    };
  };

  return (
    <div className={styles['single-chargeBacks__header']}>
      <Breadcrumbs
        linksProps={[
          {
            text: t('innerLayout.navbar.chargebacks'),
            onClickHandler: () => navigate(INNER_ROUTES.CHARGE_BACKS),
            active: true,
          },
          {
            text: t('innerLayout.chargeBacks.singleChargeBacks.trnxId', {
              id,
            }),
          },
        ]}
      />
      <div className={styles['single-chargeBacks__header-info-box']}>
        <div
          className={
            styles['single-chargeBacks__header-info-box-amount-status']
          }
        >
          <span>{currencyFormatter(amount)}</span>

          {status && <Badge size="md" {...getBadgeProps(status)} />}
        </div>

        <div className={styles['single-chargeBacks__header-info-box-date']}>
          {`${t('innerLayout.transactionsPage.singleTransactionsPage.created')}
                ${created_at && formatToLocalizedDateTime(created_at, lang)}`}
        </div>
      </div>
    </div>
  );
};

export default Header;
